'use client';

import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Button from '@/app/components/ui/Button';
import { Upload, X, FileImage, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Image from 'next/image';
import { clsx } from 'clsx';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function UploadReference() {
  const { referenceImage, referenceImageName, setReferenceImage, setCurrentStep } =
    useBuilderStore();
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null);
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 5MB limit. Please choose a smaller file.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setReferenceImage(result, file.name);
      };
      reader.readAsDataURL(file);
    },
    [setReferenceImage]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf'],
    },
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    onDropRejected: (fileRejections) => {
      const err = fileRejections[0]?.errors[0];
      if (err?.code === 'file-too-large') {
        setError('File size exceeds 5MB limit.');
      } else if (err?.code === 'file-invalid-type') {
        setError('Invalid file type. Please upload PNG, JPEG, WEBP, or PDF.');
      } else {
        setError('File rejected. Please try again.');
      }
    },
  });

  const removeImage = () => {
    setReferenceImage(null, null);
    setError(null);
  };

  const isImage = referenceImage && !referenceImageName?.endsWith('.pdf');

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Upload size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 tracking-tight">
              Upload Reference Image
            </h2>
            <p className="text-sm text-slate-400">
              Optional — helps AI generate more accurate previews
            </p>
          </div>
        </div>

        {/* Dropzone */}
        <AnimatePresence mode="wait">
          {!referenceImage ? (
            <motion.div
              key="dropzone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div
                {...getRootProps()}
                className={clsx(
                  'relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200',
                  {
                    'border-amber-400 bg-amber-50 scale-[1.02]': isDragActive && !isDragReject,
                    'border-red-400 bg-red-50': isDragReject,
                    'border-slate-200 bg-slate-50 hover:border-amber-300 hover:bg-amber-50/50':
                      !isDragActive && !isDragReject,
                  }
                )}
              >
                <input {...getInputProps()} />
                <motion.div
                  animate={{
                    scale: isDragActive ? 1.1 : 1,
                    y: isDragActive ? -8 : 0,
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="flex flex-col items-center gap-4"
                >
                  <div
                    className={clsx(
                      'w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-200',
                      {
                        'bg-amber-100': isDragActive && !isDragReject,
                        'bg-red-100': isDragReject,
                        'bg-slate-100': !isDragActive && !isDragReject,
                      }
                    )}
                  >
                    {isDragReject ? (
                      <X size={28} className="text-red-400" />
                    ) : (
                      <Upload
                        size={28}
                        className={isDragActive ? 'text-amber-500' : 'text-slate-400'}
                      />
                    )}
                  </div>

                  {isDragActive ? (
                    <div>
                      <p className="text-amber-600 font-bold text-lg">Drop it here!</p>
                      <p className="text-amber-500 text-sm">Release to upload</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-slate-700 font-bold text-base mb-1">
                        Drag & drop your reference image
                      </p>
                      <p className="text-slate-400 text-sm mb-4">
                        or click to browse files
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {['PNG', 'JPEG', 'WEBP', 'PDF'].map((type) => (
                          <span
                            key={type}
                            className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-500 shadow-sm"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                      <p className="text-slate-400 text-xs mt-3">Maximum file size: 5MB</p>
                    </div>
                  )}
                </motion.div>
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-sm text-red-500 flex items-center gap-2"
                >
                  <X size={14} className="text-red-500" />
                  {error}
                </motion.p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <div className="relative rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4">
                {/* Success badge */}
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 size={18} className="text-emerald-500" />
                  <span className="text-sm font-semibold text-emerald-700">
                    Reference image uploaded successfully
                  </span>
                </div>

                {/* File info */}
                <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-emerald-100 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileImage size={18} className="text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {referenceImageName}
                    </p>
                    <p className="text-xs text-slate-400">Reference image</p>
                  </div>
                  <button
                    onClick={removeImage}
                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 flex items-center justify-center transition-colors duration-200 shrink-0"
                    title="Remove image"
                  >
                    <X size={14} className="text-red-500" />
                  </button>
                </div>

                {/* Image preview */}
                {isImage && (
                  <div className="relative w-full h-64 rounded-xl overflow-hidden bg-slate-100">
                    <Image
                      src={referenceImage}
                      alt="Reference preview"
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </div>
                )}

                {!isImage && referenceImageName?.endsWith('.pdf') && (
                  <div className="flex items-center justify-center h-32 bg-white rounded-xl border border-slate-100">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📄</div>
                      <p className="text-sm text-slate-500 font-medium">PDF Document</p>
                      <p className="text-xs text-slate-400">{referenceImageName}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip note */}
        <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <span className="text-blue-500 shrink-0">ℹ️</span>
          <p className="text-xs text-blue-700 leading-relaxed">
            <strong>Optional step:</strong> You can skip this and proceed to AI Preview. A
            reference image helps our AI understand your vision better, but it&apos;s not required.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => setCurrentStep(2)}
            className="gap-2"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={() => setCurrentStep(4)}
            className="gap-2 min-w-40"
          >
            Next Step
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
